import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Brain, Keyboard, Trophy, Target, Zap, Home, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultsPage() {
  const { participantId } = useParams();
  const navigate = useNavigate();
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, [participantId]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/participants/${participantId}/results`);
      setResults(response.data);
    } catch (error) {
      toast.error("Gagal memuat hasil");
    } finally {
      setLoading(false);
    }
  };

  const getIQCategory = (score) => {
    if (score >= 130) return { label: "Very Superior", color: "text-purple-600" };
    if (score >= 120) return { label: "Superior", color: "text-blue-600" };
    if (score >= 110) return { label: "High Average", color: "text-green-600" };
    if (score >= 90) return { label: "Average", color: "text-slate-600" };
    if (score >= 80) return { label: "Low Average", color: "text-orange-600" };
    return { label: "Below Average", color: "text-red-600" };
  };

  const getWPMCategory = (wpm) => {
    if (wpm >= 60) return { label: "Excellent", color: "text-purple-600" };
    if (wpm >= 45) return { label: "Above Average", color: "text-green-600" };
    if (wpm >= 30) return { label: "Average", color: "text-slate-600" };
    return { label: "Below Average", color: "text-orange-600" };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <Trophy className="w-12 h-12 text-[#002FA7] mx-auto mb-4 animate-pulse" />
          <p className="text-[#475569]">Memuat hasil...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <p className="text-[#475569] mb-4">Hasil tidak ditemukan</p>
          <Button onClick={() => navigate("/")} className="bg-[#002FA7] hover:bg-[#00237C]">
            Kembali ke Beranda
          </Button>
        </div>
      </div>
    );
  }

  const iqCategory = results.iq_score ? getIQCategory(results.iq_score) : null;
  const wpmCategory = results.wpm ? getWPMCategory(results.wpm) : null;

  return (
    <div 
      className="min-h-screen bg-[#F8F9FA] py-8 px-4"
      style={{
        backgroundImage: `linear-gradient(rgba(248,249,250,0.95), rgba(248,249,250,0.95)), url('https://images.unsplash.com/photo-1602411583501-3f1a38f8cef0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHx3aGl0ZSUyMG1pbmltYWxpc3QlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc1ODMzODg1fDA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fadeIn">
          <div className="w-16 h-16 bg-[#002FA7] flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-2">
            Hasil Test Anda
          </h1>
          <p className="text-[#475569]">
            Selamat, {results.nama}! Berikut adalah hasil test Anda.
          </p>
        </div>

        {/* Results Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* IQ Score Card */}
          <div className="bg-white border border-slate-200 p-6 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-50 flex items-center justify-center">
                <Brain className="w-5 h-5 text-[#002FA7]" />
              </div>
              <span className="text-sm font-semibold text-[#475569] uppercase tracking-wider">
                Skor IQ
              </span>
            </div>
            
            <div className="text-center py-4">
              <p 
                className="font-mono text-6xl font-black text-[#002FA7] mb-2"
                data-testid="iq-score"
              >
                {results.iq_score || "-"}
              </p>
              {iqCategory && (
                <p className={`text-sm font-semibold ${iqCategory.color}`} data-testid="iq-category">
                  {iqCategory.label}
                </p>
              )}
            </div>

            {results.correct_count !== undefined && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm text-[#475569]">
                  Jawaban Benar: <span className="font-semibold text-[#0A0A0A]">{results.correct_count} / 30</span>
                </p>
              </div>
            )}
          </div>

          {/* Typing Speed Card */}
          <div className="bg-white border border-slate-200 p-6 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-50 flex items-center justify-center">
                <Keyboard className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm font-semibold text-[#475569] uppercase tracking-wider">
                Kecepatan Mengetik
              </span>
            </div>
            
            <div className="text-center py-4">
              <p 
                className="font-mono text-6xl font-black text-green-600 mb-2"
                data-testid="wpm-score"
              >
                {results.wpm || "-"}
              </p>
              <p className="text-sm text-[#475569]">Kata per Menit (WPM)</p>
              {wpmCategory && (
                <p className={`text-sm font-semibold mt-1 ${wpmCategory.color}`} data-testid="wpm-category">
                  {wpmCategory.label}
                </p>
              )}
            </div>

            {results.accuracy !== undefined && (
              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#002FA7]" />
                  <span className="text-sm text-[#475569]">
                    Akurasi: <span className="font-semibold text-[#0A0A0A]">{results.accuracy}%</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#002FA7]" />
                  <span className="text-sm text-[#475569]">
                    {results.total_characters} karakter
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Participant Info */}
        <div className="bg-white border border-slate-200 p-6 mb-8 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
          <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4">
            Informasi Peserta
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-[#475569]">Nama:</span>
              <span className="ml-2 font-medium text-[#0A0A0A]">{results.nama}</span>
            </div>
            <div>
              <span className="text-[#475569]">Email:</span>
              <span className="ml-2 font-medium text-[#0A0A0A]">{results.email}</span>
            </div>
            <div>
              <span className="text-[#475569]">Usia:</span>
              <span className="ml-2 font-medium text-[#0A0A0A]">{results.usia} tahun</span>
            </div>
            <div>
              <span className="text-[#475569]">Pendidikan:</span>
              <span className="ml-2 font-medium text-[#0A0A0A]">{results.tingkat_pendidikan}</span>
            </div>
            <div className="sm:col-span-2">
              <span className="text-[#475569]">Jurusan:</span>
              <span className="ml-2 font-medium text-[#0A0A0A]">{results.jurusan}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeIn" style={{ animationDelay: '0.4s' }}>
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="rounded-sm"
            data-testid="btn-home"
          >
            <Home className="w-4 h-4 mr-2" />
            Kembali ke Beranda
          </Button>
          <Button
            onClick={() => navigate("/")}
            className="bg-[#002FA7] hover:bg-[#00237C] rounded-sm"
            data-testid="btn-retake"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Ambil Test Lagi
          </Button>
        </div>
      </div>
    </div>
  );
}
